/**
 * The checks every step that captures a frame applies to its `captureConfig`.
 *
 * Shared between `Locate` and `RunInspectSkill` because both write through the
 * runner's `captureFrameToStorage`, which resolves one destination list and one
 * filename format. Two copies of these messages drifting apart would tell an
 * operator two different things about the same field. `RunLocateSkill` writes
 * through it too, and so far shares only the file name check.
 *
 * What is NOT here is the lookup of a named equipment id, which the two steps
 * word differently.
 */
import { inspectCountVariableKey } from '@sb/ai/types/inspectClassKeys';
import type {
  CaptureConfig,
  FileNamePart,
} from '@sb/routine-runner/Step/Capture/captureConfig';
import { splitTextVariables } from '@sb/routine-runner/Step/Capture/captureFileName';
import type { Expression } from '@sb/routine-runner/types';

import type { RoutineView, ValidationContext } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { WAIT_FOR_CONFIRMATION_OUTPUT } from '../../util/validators';
import { isMissingVariable, isMissingVariableName } from '../../util/variables';

/**
 * A capture must have somewhere to write.
 *
 * The test is that AT LEAST ONE destination exists, rather than that storage
 * equipment does. A local destination needs no equipment, and it is what the
 * shared Capture tab gives a step on a robot with no FTP or S3 device — so the
 * stricter test blocked a routine over a field that same tab reports as
 * unnecessary.
 *
 * Checked here rather than at run time, which is where it used to surface: the
 * frame is captured, every validation passes, and the step fails partway
 * through a routine.
 *
 * `?? []` because a step's stored arguments are the raw JSON. No zod default
 * fills `destinations` in, and a routine saved before the field existed has no
 * key at all.
 *
 * @throws {ActionRequiredError}
 */
export function checkCaptureDestination(captureConfig: CaptureConfig): void {
  const destinations = captureConfig.destinations ?? [];

  const hasLocal = destinations.some(
    (destination) => destination.kind === 'local',
  );

  const hasNamedEquipment =
    Boolean(captureConfig.saveEquipmentId) ||
    destinations.some(
      (destination) =>
        destination.kind === 'equipment' && Boolean(destination.equipmentId),
    );

  if (hasLocal || hasNamedEquipment) return;

  throw new ActionRequiredError({
    kind: 'invalidConfiguration',
    message:
      'This step saves images but has no destination. Choose storage equipment, or turn on saving to the robot.',
    fieldId: 'captureConfig.saveEquipmentId',
  });
}

/**
 * Whether the expression's source text names a `.png`.
 *
 * Only the two kinds whose source is visible here can be read. A variable,
 * sensor, conditional or equipment expression resolves at run time, so there is
 * nothing to check and it passes.
 */
function namesAPng(saveFilepath: Expression): boolean {
  switch (saveFilepath.kind) {
    case 'constant':
      return saveFilepath.value.toString().endsWith('.png');
    case 'JavaScript':
    case 'Python':
      // The string literal's closing quote is part of the expression source.
      return saveFilepath.expression.endsWith('.png"');
    default:
      return true;
  }
}

/**
 * The filename a capture writes under.
 *
 * An ABSENT expression is the auto-name case and passes: the runner generates a
 * unique filename. An explicitly EMPTY one is still an error, because
 * generating a name over it would present an operator's unfinished config as
 * though it had taken effect.
 *
 * @throws {ActionRequiredError}
 */
export function checkCaptureFilepath(
  saveFilepath: CaptureConfig['saveFilepath'],
): void {
  if (!saveFilepath) return;

  if (saveFilepath.kind === 'constant' && saveFilepath.value === '') {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: 'Save file path is required',
      fieldId: 'captureConfig.saveFilepath',
    });
  }

  if (!namesAPng(saveFilepath)) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: 'Save file path must end with .png',
      fieldId: 'captureConfig.saveFilepath',
    });
  }
}

/**
 * Whether a step-output part names an output its step no longer has. Only a
 * Prompt User step's mode and an Inspect step's classes change a step's
 * outputs; every other kind's are fixed.
 */
function isMissingStepOutput(
  part: Extract<FileNamePart, { kind: 'stepVariable' }>,
  routine: RoutineView,
): boolean {
  const step = routine.stepConfigurations[part.stepID];

  if (!step) return true;

  const { args } = step;

  if (args.argumentKind === 'WaitForConfirmation') {
    return (
      part.name !== 'confirmed' &&
      part.name !== WAIT_FOR_CONFIRMATION_OUTPUT[args.mode ?? 'confirm']
    );
  }

  if (
    args.argumentKind === 'RunInspectSkill' &&
    part.name.startsWith('count_')
  ) {
    return !(args.classes ?? []).some(
      ({ id }) => inspectCountVariableKey(id) === part.name,
    );
  }

  return false;
}

/**
 * The variables, steps and step outputs a capture's file name reads have to
 * still exist. A missing variable or step fails the step at play, and a
 * missing output leaves its part empty.
 *
 * @throws {ActionRequiredError}
 */
export function checkCaptureFileName(
  captureConfig: Pick<CaptureConfig, 'fileName'>,
  routine: RoutineView,
  ctx: Pick<ValidationContext, 'globalVariables'>,
  fieldId = 'captureConfig.fileName',
): void {
  const missing = (captureConfig.fileName ?? []).some((part) =>
    part.kind === 'variable'
      ? isMissingVariable(part.variableID, routine, ctx)
      : part.kind === 'stepVariable' && isMissingStepOutput(part, routine),
  );

  if (missing) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message:
        'The file name uses a variable or step that is no longer in this routine. Remove it, or choose another.',
      fieldId,
    });
  }

  const unknownName = (captureConfig.fileName ?? [])
    .flatMap((part) =>
      part.kind === 'text'
        ? splitTextVariables(part.value).filter((_, index) => index % 2 === 1)
        : [],
    )
    .find((name) => isMissingVariableName(name, routine, ctx));

  if (unknownName !== undefined) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `The file name's text uses {${unknownName}}, which is not a variable in this routine.`,
      fieldId,
    });
  }
}
